/**
 * RoofCrewApprovedSection — Shannon's "approved → mark paid" view for crew
 * payments. Sits on the Approved tab of the Commissions board (Crew Pay
 * category). Surfaces every roof_crew_payments row where status='approved' AND
 * paid_at IS NULL, grouped by vendor (Risol Construction by default).
 *
 * Per spec (memory/features/finance-board-redesign.md §244):
 *   "Same Approved → Paid lifecycle as RM commissions (Shannon marks paid in
 *    the Approved tab, grouped by vendor instead of person)."
 *
 * Mark-paid action:
 *   1. UPDATE roof_crew_payments SET status='paid', paid_at, paid_by,
 *      paid_method, paid_reference.
 *   2. INSERT finance_audit_log (is_financial=true, amount_delta=-total).
 *   3. (Oct 21, 2026) No receipt email from here any more: the
 *      vendor-payment-receipt function was retired on Oct 20 (it never
 *      delivered one). A payment recorded in Books emails Risol from Books;
 *      the Send button below writes a one-off email.
 *   4. (Oct 22, 2026) No QuickBooks Bill from here any more: the
 *      quickbooks-sync create_bill call never reached the function (the
 *      browser stopped it at the CORS preflight), so every Mark paid showed
 *      "⚠ QB Bill not posted". Bardin leaves QuickBooks for Books.
 *
 * Permissions: finance, admin, super-user can mark paid (canMarkPaid in
 * CommissionBoard). Non-finance roles see the list but no action button.
 *
 * Oct 8, 2026 — a payment approved through `roof_crew_payment_approve` has a
 * Books bill behind it (`books_bill_id`): Shannon pays it IN BOOKS and the
 * pull marks the row paid (method 'books:<method>'). Those rows show the bill
 * chip and no Mark paid; rows approved before Oct 8 keep the road above.
 *
 * Oct 20, 2026 (Greg) — the PM's pay rides the Risol rail too, but Bardin pays
 * it differently, so it has its own pill. `scope="crew"` lists the crew job
 * money (Crew Pay); `scope="pm"` lists Ramiro's per-build fees and biweekly base
 * (PM Pay). Each scope opens its own paid email. `isPmPayRow` (lib/pmPay) is
 * the one test that splits them.
 */

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { TeamRole } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import DealLink from "../DealLink";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import CrewPayPaidEmailModal from "./CrewPayPaidEmailModal";
import { BILL_CHIP_CLASS, BOOKS_PAYOUT_BILL_SELECT, billChipText, billChipTone, normalizeBill, type BooksPayoutBill } from "../../lib/booksPayoutBills";
import { fetchPmName, isPmPayRow, pmPayLabel } from "../../lib/pmPay";

interface ApprovedCrewPayment {
  id: string;
  /** null for non-job rows such as the PM biweekly base (deal_id IS NULL). */
  deal_id: string | null;
  vendor_id: string;
  vendor_name: string;
  vendor_email: string | null;
  squares: number;
  rate_per_sq: number;
  flat_fee: number;
  additional_work_amount: number;
  additional_work_description: string | null;
  total: number;
  approved_at: string | null;
  homeowner_name: string | null;
  property_street: string | null;
  claim_number: string | null;
  build_completed_date: string | null;
  /** Oct 8, 2026 — the Books bill that carries this payment, when approved through the RPC. */
  books_bill_id: string | null;
  bill: BooksPayoutBill | null;
}

type PayMethod = "check" | "ach" | "cash";

interface PayEdit {
  method: PayMethod;
  reference: string;
}

const METHOD_LABELS: Record<PayMethod, string> = {
  check: "Check",
  ach:   "ACH / wire",
  cash:  "Cash",
};

function canMarkPaid(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "finance" || role === "admin" || role === "sales_director";
}

export default function RoofCrewApprovedSection({
  role,
  isSuperUser,
  onPaid,
  scope = "crew",
}: {
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onPaid?: () => void;
  /** "crew" = the crew job money (Crew Pay pill); "pm" = the PM's pay (PM Pay pill). */
  scope?: "crew" | "pm";
}) {
  const { member } = useAuth();
  const [rows, setRows] = useState<ApprovedCrewPayment[] | null>(null);
  const [edits, setEdits] = useState<Record<string, PayEdit>>({});
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pmName, setPmName] = useState("Ramiro Perez");
  // PM Pay only: the row whose Cancel… box is open, and the reason typed in it.
  const [cancelFor, setCancelFor] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  const allowed = canMarkPaid(role, isSuperUser);
  const isPm = scope === "pm";

  useEffect(() => {
    if (isPm) fetchPmName().then(setPmName);
  }, [isPm]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error: err } = await supabase
        .from("roof_crew_payments")
        .select(`
          id, deal_id, vendor_id, squares, rate_per_sq, flat_fee,
          additional_work_amount, additional_work_description, total,
          approved_at, books_bill_id,
          deals(homeowner_name, property_street, claim_number, build_completed_date),
          vendors!inner(name, primary_email),
          bill:books_payout_bills(${BOOKS_PAYOUT_BILL_SELECT})
        `)
        .eq("status", "approved")
        .is("paid_at", null)
        .order("approved_at", { ascending: true });
      if (cancelled) return;
      if (err) { setLoadErr(err.message); setRows([]); return; }
      setLoadErr(null);
      const all: ApprovedCrewPayment[] = (data ?? []).map((r: any) => ({
        id: r.id,
        deal_id: r.deal_id,
        vendor_id: r.vendor_id,
        vendor_name: r.vendors?.name ?? "Unknown vendor",
        vendor_email: r.vendors?.primary_email ?? null,
        squares: Number(r.squares ?? 0),
        rate_per_sq: Number(r.rate_per_sq ?? 0),
        flat_fee: Number(r.flat_fee ?? 0),
        additional_work_amount: Number(r.additional_work_amount ?? 0),
        additional_work_description: r.additional_work_description,
        total: Number(r.total ?? 0),
        approved_at: r.approved_at,
        homeowner_name: r.deals?.homeowner_name ?? null,
        property_street: r.deals?.property_street ?? null,
        claim_number: r.deals?.claim_number ?? null,
        build_completed_date: r.deals?.build_completed_date ?? null,
        books_bill_id: r.books_bill_id ?? null,
        bill: (() => { const b = Array.isArray(r.bill) ? r.bill[0] : r.bill; return b ? normalizeBill(b) : null; })(),
      }));
      // Crew Pay never lists the PM's pay, and PM Pay lists nothing else.
      const list = all.filter((r) => isPmPayRow(r) === isPm);
      setRows(list);
      // Default each new row to method=check
      setEdits((prev) => {
        const next = { ...prev };
        for (const r of list) {
          if (!next[r.id]) next[r.id] = { method: "check", reference: "" };
        }
        return next;
      });
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey, isPm]);

  // Realtime: another user (Chad) approves a new payment OR another finance
  // user marks one paid → bump reloadKey so this view re-fetches.
  useRealtimeTable("roof_crew_payments", () => setReloadKey((k) => k + 1));

  async function markPaid(row: ApprovedCrewPayment) {
    if (!allowed) return;
    if (!member?.id) {
      setError("Team roster row not found — sign in as a finance/admin user.");
      return;
    }
    const e = edits[row.id] ?? { method: "check" as PayMethod, reference: "" };

    setActing((s) => new Set(s).add(row.id));
    setError(null);
    try {
      const now = new Date().toISOString();
      const { error: updateErr } = await supabase
        .from("roof_crew_payments")
        .update({
          status: "paid",
          paid_at: now,
          paid_by: member.id,
          paid_method: e.method,
          paid_reference: e.reference.trim() || null,
        })
        .eq("id", row.id);
      if (updateErr) throw updateErr;

      // Audit log
      await supabase.from("finance_audit_log").insert({
        deal_id: row.deal_id,
        actor_id: member.id,
        action: "roof_crew_payment_paid",
        is_financial: true,
        amount_delta: -row.total,
        after_jsonb: {
          payment_id: row.id,
          vendor_id: row.vendor_id,
          vendor_name: row.vendor_name,
          total: row.total,
          method: e.method,
          reference: e.reference.trim() || null,
        },
        reason: `Crew payment paid to ${row.vendor_name} via ${METHOD_LABELS[e.method]}` +
                (e.reference.trim() ? ` (ref ${e.reference.trim()})` : ""),
      });

      setInfo(`✓ Paid ${fmtUSD(row.total)} to ${row.vendor_name}.`);

      // Refresh local list and notify parent (which reloads the deals query
      // so other sections can react).
      setReloadKey((k) => k + 1);
      onPaid?.();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(row.id); return n; });
    }
  }

  // Oct 26, 2026: a PM fee that is not owed is cancelled (pm_fee_cancel voids the row and its Books bill; Books
  // removes the entry). Refused once Books has paid the bill.
  async function cancelPmFee(row: ApprovedCrewPayment) {
    if (!allowed) return;
    setActing((s) => new Set(s).add(row.id));
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc("pm_fee_cancel", { p_ids: [row.id], p_reason: cancelReason.trim() });
      if (rpcErr) throw rpcErr;
      setInfo(`✓ Cancelled ${fmtUSD(row.total)} (${pmPayLabel(row)})${row.books_bill_id ? " and took its bill back from Books" : ""}.`);
      setCancelFor(null);
      setCancelReason("");
      setReloadKey((k) => k + 1);
      onPaid?.();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(row.id); return n; });
    }
  }

  // The paid email for this pill — always reachable, even when nothing is
  // awaiting disbursement (the email is about payments already marked paid
  // that day). Crew Pay sends the job email, PM Pay sends the PM's own email.
  const emailBar = allowed ? (
    <div className="flex justify-end">
      <button
        onClick={() => setEmailOpen(true)}
        className="px-3 py-1.5 text-xs font-medium rounded border border-bardin-orange text-bardin-orange hover:bg-orange-50"
      >
        {isPm ? `✉️ Send ${pmName} PM pay email` : "✉️ Send crew pay paid email"}
      </button>
    </div>
  ) : null;
  const emailModal = (
    <CrewPayPaidEmailModal kind={scope} open={emailOpen} onClose={() => setEmailOpen(false)} />
  );

  if (rows === null) {
    return (
      <div className="space-y-3">
        {emailBar}
        <div className="text-sm text-slate-500 p-4">
          {isPm ? "Loading PM pay…" : "Loading approved crew payments…"}
        </div>
        {emailModal}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {emailBar}
        {loadErr ? (
          // A failed read must never read as "nothing owed".
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-xl">
            ⚠ Couldn't load {isPm ? "PM pay" : "crew payments"} — {loadErr}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
            {isPm
              ? `No ${pmName} PM pay awaiting disbursement.`
              : "No approved crew payments awaiting disbursement."}
          </div>
        )}
        {emailModal}
      </div>
    );
  }

  // Group by vendor for display
  const byVendor: Record<string, { name: string; email: string | null; rows: ApprovedCrewPayment[]; total: number }> = {};
  for (const r of rows) {
    if (!byVendor[r.vendor_id]) {
      byVendor[r.vendor_id] = { name: r.vendor_name, email: r.vendor_email, rows: [], total: 0 };
    }
    byVendor[r.vendor_id].rows.push(r);
    byVendor[r.vendor_id].total += r.total;
  }
  const vendorGroups = Object.values(byVendor).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      {emailBar}
      {error && (
        <div className="bg-red-50 text-red-700 text-xs p-2 rounded">⚠ {error}</div>
      )}
      {info && (
        <div className="bg-emerald-50 text-emerald-800 text-xs p-2 rounded flex items-start justify-between gap-3">
          <span className="flex-1">{info}</span>
          <button
            onClick={() => setInfo(null)}
            className="text-emerald-700 hover:text-emerald-900 text-sm font-bold leading-none"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {vendorGroups.map((g) => (
        <div key={g.name} className="bg-white rounded-xl shadow">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-bardin-ink">
                {isPm ? `${g.name} — PM pay for ${pmName}` : g.name}
              </h3>
              <p className="text-[11px] text-slate-500">
                {isPm ? (
                  <>
                    {g.rows.length} PM payment{g.rows.length === 1 ? "" : "s"} · paid separately from the crew money
                    {g.email ? ` · PM pay email to ${g.email}` : " · no email on file"}
                  </>
                ) : (
                  <>
                    {g.rows.length} approved payment{g.rows.length === 1 ? "" : "s"}
                    {g.email ? ` · email on file: ${g.email}` : " · no email on file"}
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{isPm ? "PM pay total" : "Vendor total"}</div>
              <div className="text-lg font-extrabold text-bardin-orange">{fmtUSD(g.total)}</div>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {g.rows.map((r) => {
              const e = edits[r.id] ?? { method: "check" as PayMethod, reference: "" };
              const isActing = acting.has(r.id);
              return (
                <div key={r.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {r.deal_id ? (
                          <DealLink id={r.deal_id}>{r.homeowner_name ?? "(no name)"}</DealLink>
                        ) : (
                          <span>{r.additional_work_description ?? "Non-job payment"}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {r.deal_id ? (
                          <>
                            {r.property_street ?? "—"}
                            {r.claim_number ? ` · #${r.claim_number}` : ""}
                            {r.build_completed_date ? ` · Built ${r.build_completed_date.slice(0, 10)}` : ""}
                          </>
                        ) : isPm ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">Not job-specific</span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">Not job-specific</span>
                            <span>Pay it with this vendor's batch</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[11px] text-slate-500">
                        {isPm
                          ? pmPayLabel(r)
                          : r.deal_id
                          ? `${r.squares} sq × $${Number(r.rate_per_sq).toFixed(2)} + $${Number(r.flat_fee).toFixed(2)}${
                              r.additional_work_amount > 0 ? ` + $${Number(r.additional_work_amount).toFixed(2)}` : ""
                            }`
                          : "Flat amount"}
                      </div>
                      <div className="text-base font-bold text-bardin-ink">{fmtUSD(r.total)}</div>
                    </div>
                  </div>

                  {!isPm && r.additional_work_description && (
                    <div className="text-[11px] text-slate-600 italic bg-slate-50 px-2 py-1 rounded mb-2">
                      Additional work: "{r.additional_work_description}"
                    </div>
                  )}

                  {r.books_bill_id ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span
                        title={r.bill ? billChipText(r.bill) : "Bill in Books"}
                        className={"inline-block text-[11px] font-semibold px-2 py-1 rounded " + BILL_CHIP_CLASS[r.bill ? billChipTone(r.bill) : "sent"]}
                      >
                        {r.bill ? billChipText(r.bill) : "Bill in Books"}
                      </span>
                      <span className="text-[11px] text-slate-400 italic">Shannon pays it in Books (Banking → Transactions); the row reads paid once Books reports back.</span>
                    </div>
                  ) : allowed ? (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <select
                        value={e.method}
                        onChange={(ev) =>
                          setEdits((prev) => ({
                            ...prev,
                            [r.id]: { ...prev[r.id], method: ev.target.value as PayMethod },
                          }))
                        }
                        disabled={isActing}
                        className="text-xs border border-slate-300 rounded px-2 py-1"
                      >
                        {(Object.keys(METHOD_LABELS) as PayMethod[]).map((m) => (
                          <option key={m} value={m}>{METHOD_LABELS[m]}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder={e.method === "check" ? "Check #" : e.method === "ach" ? "Wire ref (optional)" : "Reference (optional)"}
                        value={e.reference}
                        onChange={(ev) =>
                          setEdits((prev) => ({
                            ...prev,
                            [r.id]: { ...prev[r.id], reference: ev.target.value },
                          }))
                        }
                        disabled={isActing}
                        className="text-xs border border-slate-300 rounded px-2 py-1 flex-1 min-w-[140px]"
                      />
                      <button
                        onClick={() => markPaid(r)}
                        disabled={isActing}
                        className="px-3 py-1 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {isActing ? "Marking paid…" : "Mark paid"}
                      </button>
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400 italic mt-2">
                      Awaiting Shannon (finance) to mark paid.
                    </div>
                  )}

                  {isPm && allowed && (
                    cancelFor === r.id ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-red-200 bg-red-50 p-2">
                        <span className="text-[11px] text-red-800 w-full">
                          Cancel this fee: it is never paid{r.books_bill_id ? ", and its bill is taken back from Books (any other fees on that bill go to Books as a new bill)" : ""}.
                        </span>
                        <input
                          type="text"
                          placeholder="Why (required)"
                          value={cancelReason}
                          onChange={(ev) => setCancelReason(ev.target.value)}
                          disabled={isActing}
                          className="text-xs border border-slate-300 rounded px-2 py-1 flex-1 min-w-[160px]"
                        />
                        <button
                          onClick={() => cancelPmFee(r)}
                          disabled={isActing || cancelReason.trim().length < 5}
                          className="px-3 py-1 text-xs font-medium rounded bg-red-600 text-white hover:opacity-90 disabled:opacity-50"
                        >
                          {isActing ? "Cancelling…" : "Cancel fee"}
                        </button>
                        <button
                          onClick={() => { setCancelFor(null); setCancelReason(""); }}
                          disabled={isActing}
                          className="px-2 py-1 text-xs text-slate-600 hover:underline"
                        >
                          Keep it
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setCancelFor(r.id); setCancelReason(""); }}
                        className="mt-2 text-[11px] text-slate-500 hover:text-red-700 hover:underline"
                      >
                        Cancel…
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {emailModal}
    </div>
  );
}
